import { CrossIcon, InfoIcon } from "@cynthai/icons";
import { Breadcrumbs, Chip, Divider, SearchFilterBar } from "@cynthai/ui";
import { useDebounceFn } from "@reactuses/core";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import z from "zod";

import PageContainer from "@/core/ui/layout/PageContainer";
import ArticleCard from "@/features/help-center/components/ArticleCard";
import Autosuggest from "@/features/help-center/components/AutoSuggest";
import SearchResultsSkeleton from "@/features/help-center/components/LoadingSkeleton";
import { useArticles } from "@/features/help-center/hooks/use-articles";
import { m } from "@/paraglide/messages";

const searchSchema = z.object({
  query: z.string().optional(),
  topic: z.string().optional(),
});

export const Route = createFileRoute("/_protected/help-center/search/")({
  component: () => <HelpCenterSearch />,
  validateSearch: searchSchema,
});

function HelpCenterSearch() {
  const navigate = Route.useNavigate();
  const { query: urlQuery, topic: urlTopic } = Route.useSearch();
  const [showAutosuggest, setShowAutosuggest] = useState(false);

  const { data: articles = [], isLoading } = useArticles({
    search: urlQuery,
    topic: urlTopic,
    published_only: true,
  });

  const { run: debouncedSearch } = useDebounceFn((value: string) => {
    const query = value.trim() || undefined;
    navigate({ search: { query }, replace: true });
  }, 300);

  const handleSearch = (value: string) => {
    const query = value.trim();
    if (!query) {
      clearSearch();
      return;
    }
    setShowAutosuggest(false);
    navigate({ search: { query }, replace: true });
  };

  const clearSearch = () => {
    setShowAutosuggest(false);
    navigate({ to: "/help-center", replace: true });
  };

  const infoIcon = <InfoIcon height={22} width={22} />;
  const searchParams = new URLSearchParams();
  if (urlQuery) searchParams.set("query", urlQuery);
  if (urlTopic) searchParams.set("topic", urlTopic);
  const breadcrumbs = [
    { href: "/help-center", label: infoIcon },
    {
      href: `/help-center/search?${searchParams.toString()}`,
      label: m.search_results_breadcrumb(),
    },
  ];

  const hasSearchQuery = Boolean(urlQuery || urlTopic);

  return (
    <PageContainer>
      <Breadcrumbs
        items={breadcrumbs}
        className={`mx-auto flex w-full justify-start`}
      />
      <div className="mx-auto w-full">
        <h1
          className={`text-text-primary my-4 text-center text-2xl font-semibold`}
        >
          {m.how_can_we_help()}
        </h1>

        <div className="flex items-center justify-center">
          <div className="w-full max-w-2xl">
            <div className="relative">
              {urlTopic ? (
                <div
                  className={`bg-background-2 flex items-center gap-2 rounded-lg p-1.75`}
                >
                  <div
                    className={`border-tertiary flex-1 space-y-0.5 rounded-md border text-sm`}
                  >
                    <div
                      className={`bg-secondary text-text-secondary flex h-9 flex-row items-center justify-between gap-4 rounded-md p-2 text-sm`}
                    >
                      <Chip
                        size="sm"
                        className={`bg-primary/20 text-text-primary flex max-w-full truncate rounded-lg border-none`}
                      >
                        {urlTopic}
                      </Chip>
                      <button
                        type="button"
                        aria-label="Remove filter"
                        onClick={clearSearch}
                        className={`text-text-secondary hover:text-text-primary ml-4 flex cursor-pointer`}
                      >
                        <CrossIcon height={12} width={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <SearchFilterBar
                    searchbarProps={{
                      className: "text-sm",
                      color: "tertiary",
                      placeholder: m.search_help_center(),
                      defaultValue: urlQuery ?? "",
                      onChange: (e) => debouncedSearch(e.target.value),
                      onFocus: () => setShowAutosuggest(true),
                      onBlur: () => setShowAutosuggest(false),
                      onKeyDown: (e) => {
                        if (e.key === "Enter")
                          handleSearch(e.currentTarget.value);
                      },
                    }}
                  />
                  {showAutosuggest && (
                    <div
                      className={`pointer-events-none absolute inset-x-0 flex justify-center`}
                    >
                      {/* preventDefault on pointerdown keeps Searchbar focused while clicking suggestions */}
                      <div
                        className="pointer-events-auto w-full max-w-2xl"
                        onPointerDown={(e) => e.preventDefault()}
                      >
                        <Autosuggest
                          query={urlQuery ?? ""}
                          onSelect={(suggestion) => {
                            setShowAutosuggest(false);
                            handleSearch(suggestion);
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <Divider className="mx-auto my-12" />

        <div className="mx-auto">
          {hasSearchQuery && (
            <>
              <h1
                className={`text-text-primary mx-auto mb-10 text-2xl font-semibold`}
              >
                {m.search_results({ query: (urlQuery || urlTopic) ?? "" })}
              </h1>
              <p className="text-text-secondary text-start">
                {m.related_articles()}{" "}
                <span
                  className={`bg-primary/20 text-text-primary ml-2 rounded-md px-3 py-2 font-medium`}
                >
                  {m.related_results({ count: articles.length })}
                </span>
              </p>
            </>
          )}

          {isLoading ? (
            <SearchResultsSkeleton count={6} />
          ) : hasSearchQuery && articles.length === 0 ? (
            <div
              className={`border-tertiary bg-background-2 mt-10 flex flex-col items-center justify-center rounded-lg border px-4 py-12 text-center`}
            >
              <p className="text-text-primary mb-2 text-lg font-medium">
                {m.no_search_results()}
              </p>
              <p className="text-text-secondary">
                {m.no_search_results_description()}
              </p>
            </div>
          ) : (
            hasSearchQuery && (
              <div className={`grid grid-cols-2 gap-4 py-10 2xl:grid-cols-3`}>
                {articles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </PageContainer>
  );
}
